import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Per-project notification recipients picker.
 *
 * Lists every active user in a checkbox grid; toggling and saving updates
 * `project.notify_user_ids`. Those users will receive in-app toasts when
 * the closeout PDF is ready, when the project is approved, and on
 * overdue-safety / missing-photos reminders.
 *
 * Visible to anyone who can view the project; saving is gated by the
 * backend to foreman / pm / admin.
 */
export default function RecipientsCard({ projectId, canEdit }) {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const [dirRes, recRes] = await Promise.all([
          api.get("/users/directory"),
          api.get(`/projects/${projectId}/recipients`),
        ]);
        if (cancelled) return;
        setUsers(dirRes.data || []);
        setSelected(new Set((recRes.data || []).map((u) => u.id)));
      } catch {
        if (!cancelled) toast.error("Failed to load recipients");
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const toggle = (uid) => {
    if (!canEdit) return;
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelected(next);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/recipients`, {
        user_ids: Array.from(selected),
      });
      toast.success("Recipients updated");
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save recipients");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="recipients-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-400" />
            <div className="font-display font-bold text-lg">Notification Recipients</div>
          </div>
          {canEdit && (
            <Button
              size="sm"
              onClick={save}
              disabled={!dirty || saving}
              className="bg-emerald-700 hover:bg-emerald-600 font-display tracking-wider"
              data-testid="save-recipients"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              SAVE
            </Button>
          )}
        </div>
        <div className="text-xs text-slate-400 leading-snug">
          Selected users get an in-app toast when the closeout PDF is ready,
          when this project is approved, and on overdue-safety / missing-photo
          reminders. They will see pending notifications the next time they
          sign in.
        </div>
        {busy ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> loading users…
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-64 overflow-auto pr-1">
            {users.map((u) => {
              const on = selected.has(u.id);
              return (
                <label
                  key={u.id}
                  className={`flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer border ${
                    on ? "border-emerald-700 bg-emerald-950/30" : "border-slate-800 hover:bg-slate-800/40"
                  }`}
                  data-testid={`recipient-row-${u.id}`}
                >
                  <Checkbox
                    checked={on}
                    onCheckedChange={() => toggle(u.id)}
                    disabled={!canEdit}
                    data-testid={`recipient-toggle-${u.id}`}
                  />
                  <div className="min-w-0">
                    <div className="text-sm text-slate-100 truncate">{u.full_name}</div>
                    <div className="text-[11px] text-slate-500 truncate font-mono">
                      {u.email} · {u.role}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
